import PropTypes from 'prop-types';
import { createContext, useEffect, useReducer } from 'react';
import { isMfaRequiredResponse } from '@siptel-web/common';
// utils
import axios from '../utils/axios';
import { getUser, isValidToken, setSession, setUser } from '../utils/jwt';

// ----------------------------------------------------------------------

const initialState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
};

const handlers = {
  INITIALIZE: (state, action) => {
    const { isAuthenticated, user } = action.payload;
    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      user,
    };
  },
  LOGIN: (state, action) => {
    const { user } = action.payload;

    return {
      ...state,
      isAuthenticated: true,
      user,
    };
  },
  LOGIN_WITH_TOKEN: (state, action) => {
    const { user } = action.payload;

    return {
      ...state,
      isAuthenticated: true,
      user,
    };
  },
  LOGOUT: (state) => ({
    ...state,
    isAuthenticated: false,
    user: null,
  }),
  ADD_PROFILE_PIC: (state, action) => ({
    ...state,
    user: {
      ...state.user,
      user_img_perfil: action.payload,
    },
  }),
  EDIT_DATA_USER: (state, action) => ({
    ...state,
    user: {
      ...state.user,
      ...action.payload.dataUserEdited,
    },
  }),
  REGISTER: (state, action) => {
    const { user } = action.payload;

    return {
      ...state,
      isAuthenticated: true,
      user,
    };
  },
};

const reducer = (state, action) =>
  handlers[action.type] ? handlers[action.type](state, action) : state;

const AuthContext = createContext({
  ...initialState,
  method: 'jwt',
  login: () => Promise.resolve(),
  verifyMfa: () => Promise.resolve(),
  resendMfa: () => Promise.resolve(),
  logout: () => Promise.resolve(),
});

// ----------------------------------------------------------------------

AuthProvider.propTypes = {
  children: PropTypes.node,
};

function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    const initialize = async () => {
      try {
        const authToken = localStorage.getItem('authToken');
        const user = getUser();

        if (authToken && isValidToken(authToken) && user) {
          setSession(authToken);

          dispatch({
            type: 'INITIALIZE',
            payload: {
              isAuthenticated: true,
              user,
            },
          });
        } else {
          dispatch({
            type: 'INITIALIZE',
            payload: {
              isAuthenticated: false,
              user: null,
            },
          });
        }
      } catch (err) {
        console.error(err);
        dispatch({
          type: 'INITIALIZE',
          payload: {
            isAuthenticated: false,
            user: null,
          },
        });
      }
    };

    initialize();
  }, []);
  const editDataUser = async (newDataUser) => {
    try {
      const dataUserEdited = {
        user_email: newDataUser.email,
        user_nombre: newDataUser.nombre,
        user_direccion: newDataUser.direccion,
        /*  user_telefono: response.data.telefono, */
      };

      const user = getUser();
      setUser({ ...user, ...dataUserEdited });
      dispatch({
        type: 'EDIT_DATA_USER',
        payload: {
          dataUserEdited,
        },
      });
    } catch (e) {
      return { e };
    }
  };
  // Post-login común (login, verifyMfa y loginWithToken): persiste el JWT
  // final y el usuario, y actualiza el estado de auth con el tipo de acción.
  const completeLogin = ({ authToken, user }, type) => {
    setSession(authToken);
    setUser(user);

    dispatch({
      type,
      payload: {
        user,
      },
    });
  };

  // Devuelve { mfaRequired: true, sessionTemp, mfaMethod } si el usuario
  // requiere segundo factor; el desafío NO se guarda en el contexto ni en
  // storage (solo lo mantiene en memoria quien llama).
  const login = async (signInDto) => {
    const response = await axios.post('/auth/signin', signInDto);

    if (isMfaRequiredResponse(response.data)) {
      const { session_temp: sessionTemp, mfa_method: mfaMethod } =
        response.data.mfa;
      return { mfaRequired: true, sessionTemp, mfaMethod };
    }

    completeLogin(response.data, 'LOGIN');
    return { mfaRequired: false };
  };

  const verifyMfa = async ({ sessionTemp, code }) => {
    const response = await axios.post('/auth/verify-mfa', {
      session_temp: sessionTemp,
      mfa_code: code,
    });
    completeLogin(response.data, 'LOGIN');
  };

  // Devuelve ResendMfaResponse ({ success, message, resends_remaining, retry_after_seconds }).
  const resendMfa = async ({ sessionTemp }) => {
    const response = await axios.post('/auth/resend-mfa', {
      session_temp: sessionTemp,
    });
    return response.data;
  };

  const loginWithToken = async (signInDto) => {
    const response = await axios.post('auth/signin-with-token', signInDto);
    completeLogin(response.data, 'LOGIN_WITH_TOKEN');
  };

  const logout = async () => {
    sessionStorage.clear();
    localStorage.clear();
    setSession(null);
    setUser(null);
    dispatch({ type: 'LOGOUT' });
    window.location.reload(false);
  };
  const addProfilePic = (value) => {
    dispatch({
      type: 'ADD_PROFILE_PIC',
      payload: value,
    });
  };
  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: 'jwt',
        login,
        verifyMfa,
        resendMfa,
        loginWithToken,
        logout,
        addProfilePic,
        editDataUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export { AuthContext, AuthProvider };
